import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ChevronRight, ChevronDown, Ambulance, CircleUser, Zap, Building2, Shield, Heart, Award, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import Hero3D from '../components/Hero3D';

const SERVICES = [
  {
    id: 'ambulatory',
    title: 'Ambulatory',
    description: 'For patients who can walk with minimal assistance',
    icon: Ambulance,
    image: 'https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/jis3ed2t_IMG_8602.jpeg'
  },
  {
    id: 'wheelchair',
    title: 'Wheelchair',
    description: 'Safe transport for manual wheelchair users',
    icon: CircleUser,
    image: 'https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/2ueiqnut_IMG_8600.jpeg'
  },
  {
    id: 'electric_wheelchair',
    title: 'Electric Wheelchair',
    description: 'Equipped vehicles for power wheelchair transport',
    icon: Zap,
    image: 'https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/f67w74iq_IMG_8601.jpeg'
  },
  {
    id: 'hospital_discharge',
    title: 'Hospital Discharge',
    description: 'Reliable transport after hospital stays',
    icon: Building2,
    image: 'https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/1wdyddf9_IMG_9564.jpeg'
  }
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body selection:bg-red-600 selection:text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/1iyuca53_IMG_9172.png"
                alt="MarcoPolo Logo"
                className="w-12 h-12 object-contain rounded-full ring-1 ring-red-500/40"
              />
              <div>
                <h1 className="font-display text-lg font-extrabold text-white leading-tight tracking-tight">MarcoPolo</h1>
                <p className="text-xs text-neutral-400">Mobility Bridge Transportation</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-neutral-200 hover:text-red-500 font-medium transition-colors duration-300">Services</a>
              <a href="#contact" className="text-neutral-200 hover:text-red-500 font-medium transition-colors duration-300">Contact</a>
              <Link to="/careers" data-testid="nav-careers-link" className="text-neutral-200 hover:text-red-500 font-medium transition-colors duration-300">Careers</Link>
              <Link to="/booking">
                <Button data-testid="nav-book-now-btn" className="bg-red-600 hover:bg-red-500 text-white rounded-full px-6 font-semibold shadow-lg shadow-red-900/40 transition-all duration-300 hover:-translate-y-0.5 focus:ring-2 focus:ring-red-500">
                  Book Now
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 animate-fadeIn">
            <div className="px-4 py-4 space-y-3">
              <a href="#services" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-neutral-200 hover:text-red-500 font-medium">Services</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-neutral-200 hover:text-red-500 font-medium">Contact</a>
              <Link to="/careers" data-testid="mobile-careers-link" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-neutral-200 hover:text-red-500 font-medium">Careers</Link>
              <Link to="/booking" className="block">
                <Button data-testid="mobile-book-now-btn" className="w-full bg-red-600 hover:bg-red-500 text-white rounded-full font-semibold shadow-lg">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* 3D Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* 3D / fallback background */}
        <Hero3D />
        {/* Legibility overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-red-600/15 text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-7 border border-red-500/30 backdrop-blur-sm animate-fadeInUp">
              <Shield className="w-4 h-4" />
              Licensed &amp; Insured in Washington State
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight mb-6 animate-fadeInUp stagger-1 [text-shadow:_0_2px_30px_rgba(0,0,0,0.8)]">
              Safe &amp; Reliable
              <span className="block text-red-500">Medical Transport</span>
            </h1>
            <p className="text-lg sm:text-xl text-neutral-300 mb-9 max-w-xl leading-relaxed animate-fadeInUp stagger-2">
              Professional non-emergency medical transportation serving all of Washington State —
              from routine appointments to hospital discharges, we&apos;re here for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp stagger-3">
              <Link to="/booking">
                <Button data-testid="hero-book-ride-btn" className="w-full sm:w-auto bg-red-600 hover:bg-red-500 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl shadow-red-900/40 transition-all duration-300 hover:-translate-y-1 focus:ring-2 focus:ring-red-500">
                  Book Your Ride
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:+12534877444">
                <Button data-testid="hero-call-btn" variant="outline" className="w-full sm:w-auto border-2 border-white/20 hover:border-red-500 text-white bg-white/5 hover:bg-red-600/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-1">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Us Now
                </Button>
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-8 mt-12 pt-10 border-t border-white/10 animate-fadeInUp stagger-4">
              {[
                { icon: Award, value: '500+', label: 'Miles Coverage' },
                { icon: Heart, value: '24/7', label: 'Availability' },
                { icon: Shield, value: '100%', label: 'Insured' }
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-600/15 border border-red-500/20 rounded-xl flex items-center justify-center">
                    <stat.icon className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="font-display text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-sm text-neutral-400">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <a href="#services" className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center text-neutral-400 hover:text-red-500 transition-colors" aria-label="Scroll to services">
          <span className="text-xs tracking-widest uppercase mb-1">Explore</span>
          <ChevronDown className="w-5 h-5 animate-scroll-hint" />
        </a>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 lg:py-32 bg-[#0A0A0A] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">Our Services</h2>
            <p className="text-lg text-neutral-400 leading-relaxed">
              Comprehensive medical transportation solutions tailored to your specific needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((service) => (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="group bg-[#171717] rounded-2xl overflow-hidden border border-white/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(220,38,38,0.25)] hover:border-red-500/40"
              >
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#171717] via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-red-600/15 border border-red-500/20 rounded-xl flex items-center justify-center mb-4">
                    <service.icon className="w-6 h-6 text-red-500" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-2">{service.title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/booking">
              <Button data-testid="services-book-btn" className="bg-red-600 hover:bg-red-500 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl shadow-red-900/40 transition-all duration-300 hover:-translate-y-1 focus:ring-2 focus:ring-red-500">
                Book a Service
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-red-700 to-red-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
            Ready to Book Your Ride?
          </h2>
          <p className="text-xl text-red-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            Schedule your non-emergency medical transport today. Online booking available 24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button data-testid="cta-book-online-btn" className="w-full sm:w-auto bg-white hover:bg-neutral-100 text-red-700 rounded-full px-8 py-6 text-lg font-semibold shadow-xl transition-all duration-300 hover:-translate-y-1">
                Book Online Now
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:+12534877444">
              <Button data-testid="cta-call-btn" variant="outline" className="w-full sm:w-auto border-2 border-white/40 hover:border-white text-white bg-white/5 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-1 hover:bg-white/15">
                <Phone className="w-5 h-5 mr-2" />
                (253) 487-7444
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">Get in Touch</h2>
              <p className="text-lg text-neutral-400 mb-8 leading-relaxed">
                Have questions? We&apos;re here to help. Contact us anytime for assistance with your transportation needs.
              </p>

              <div className="space-y-6">
                <a href="tel:+12534877444" className="flex items-center gap-4 group" data-testid="contact-phone">
                  <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Phone</p>
                    <p className="text-xl font-semibold text-white group-hover:text-red-400 transition-colors">+1 (253) 487-7444</p>
                  </div>
                </a>

                <a href="mailto:info@marcopolonemt.com" className="flex items-center gap-4 group" data-testid="contact-email">
                  <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Email</p>
                    <p className="text-xl font-semibold text-white group-hover:text-red-400 transition-colors">info@marcopolonemt.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-400">Service Area</p>
                    <p className="text-xl font-semibold text-white">Washington State</p>
                    <p className="text-neutral-400">Up to 500 miles coverage</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#171717] rounded-3xl p-8 lg:p-12 border border-white/10">
              <h3 className="font-display text-2xl font-bold text-white mb-6">Service Hours</h3>
              <div className="space-y-4">
                {[
                  ['Monday - Friday', '24 Hours'],
                  ['Saturday', '24 Hours'],
                  ['Sunday', '24 Hours'],
                  ['Holidays', 'Available']
                ].map(([day, hrs], i, arr) => (
                  <div key={day} className={`flex justify-between py-3 ${i < arr.length - 1 ? 'border-b border-white/10' : ''}`}>
                    <span className="text-neutral-400">{day}</span>
                    <span className="text-white font-medium">{hrs}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 bg-red-600/15 rounded-xl border border-red-500/30">
                <p className="text-red-300 text-sm">
                  <strong>Note:</strong> For immediate assistance or last-minute bookings, please call us directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/1iyuca53_IMG_9172.png"
                alt="MarcoPolo Logo"
                className="w-10 h-10 object-contain rounded-full"
              />
              <div>
                <p className="font-display text-white font-bold">MarcoPolo Mobility Bridge</p>
                <p className="text-neutral-500 text-sm">Transportation LLC</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-neutral-400 text-sm">
              <Link to="/admin" className="hover:text-white transition-colors">Admin</Link>
              <span>© 2026 All rights reserved</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
